import axios from 'axios';

import { getItem, removeItem, setItem } from '@/common/localStorage';
import { errorToast } from '@/common/toast';
import * as users from './users';
import * as group from './groups';

export const adminApi = axios.create({
  baseURL: import.meta.env.VITE_API_KEY,
});

const api = axios.create({
  baseURL: import.meta.env.VITE_API_KEY,
});

// 토큰 재발급 상태 관리
let isTokenRefreshing = false;
let refreshSubscribers = [];
let navigateCallback = null;
let authAtomUpdateCallback = null;

export function setNavigateCallback(callback) {
  navigateCallback = callback;
}

export function setAuthAtomUpdateCallback(callback) {
  authAtomUpdateCallback = callback;
}

// authAtom 리셋 함수
const resetAuthAtom = () => {
  const defaultAuthState = {
    userId: '',
    username: '',
    role: 1,
    token: '',
    refreshToken: '',
  };
  
  if (authAtomUpdateCallback) {
    authAtomUpdateCallback(defaultAuthState);
  }
  
  // sessionStorage도 초기화
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('authAtom', JSON.stringify(defaultAuthState));
  }
};

// 토큰 재발급 완료 시 대기 중인 요청들 처리
const onTokenRefreshed = (accessToken) => {
  refreshSubscribers.forEach((callback) => callback(accessToken));
  refreshSubscribers = [];
};

// 토큰 재발급 대기 중인 요청 추가
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Request Interceptor: 요청 전에 토큰 추가
adminApi.interceptors.request.use(
  (config) => {
    const localToken = getItem('token');
    
    if (localToken) {
      config.headers.Authorization = localToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: 401 에러 시 토큰 재발급 처리
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    originalRequest._retry = originalRequest._retry || false;

    // 401 에러가 아니거나 이미 재시도한 요청이면 그대로 reject
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // 토큰 재발급이 진행 중이면 대기열에 추가
    if (isTokenRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((newAccessToken) => {
          // 토큰 재발급 실패 시 빈 문자열이 전달됨
          if (!newAccessToken) {
            reject(new Error('토큰 재발급 실패'));
            return;
          }
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = newAccessToken;
          }
          adminApi(originalRequest)
            .then((res) => resolve(res))
            .catch((err) => reject(err));
        });
      });
    }

    // 토큰 재발급 시작
    originalRequest._retry = true;
    isTokenRefreshing = true;

    const refreshToken = getItem('refreshToken');

    // refreshToken이 없으면 로그아웃 처리
    if (!refreshToken) {
      isTokenRefreshing = false;
      resetAuthAtom();
      removeItem('token');
      removeItem('refreshToken');
      delete adminApi.defaults.headers.common.Authorization;
      
      if (navigateCallback) {
        navigateCallback('/login');
      }
      return Promise.reject(error);
    }

    try {
      const response = await api.post(
        '/api/refresh-token',
        {},
        {
          headers: {
            Refresh: refreshToken,
          },
        }
      );
      const newAccessToken = response.headers.authorization;
      
      if (newAccessToken) {
        // 새 토큰 저장
        setItem('token', newAccessToken);
        adminApi.defaults.headers.common.Authorization = newAccessToken;
        
        // authAtom 업데이트 (callback을 통해)
        if (authAtomUpdateCallback) {
          const currentAuthState = JSON.parse(sessionStorage.getItem('authAtom') || '{}');
          const updatedAuthState = {
            ...currentAuthState,
            token: newAccessToken,
          };
          authAtomUpdateCallback(updatedAuthState);
        }
        
        // 대기 중인 요청들 처리
        onTokenRefreshed(newAccessToken);
        
        // 원래 요청 재시도
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = newAccessToken;
        }
        isTokenRefreshing = false;
        
        return adminApi(originalRequest);
      } else {
        throw new Error('토큰 재발급 실패: 응답에 토큰이 없습니다.');
      }
    } catch (refreshError) {
      isTokenRefreshing = false;
      
      // refreshToken도 만료된 경우 로그아웃 처리
      const axiosError = refreshError;
      if (axiosError.response?.status === 401 || !axiosError.response) {
        errorToast('토큰이 유효하지 않습니다. 로그아웃 됩니다.');
        resetAuthAtom();
        removeItem('token');
        removeItem('refreshToken');
        delete adminApi.defaults.headers.common.Authorization;
        
        // 대기 중인 요청들 모두 실패 처리
        const failedSubscribers = [...refreshSubscribers];
        refreshSubscribers = [];
        failedSubscribers.forEach((callback) => {
          // 빈 문자열을 전달하여 요청이 실패하도록 함
          callback('');
        });
        
        if (navigateCallback) {
          navigateCallback('/login');
        }
      }
      
      return Promise.reject(refreshError);
    }
  }
);

export { users, group };
